import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { KpiKey, KpiSummary } from '../../lib/kpi';
import { formatCents, formatCount, formatShortDay } from './format';
import { chartableKpis, KPI_CATALOG } from './kpiCatalog';

type Props = {
  summary: KpiSummary;
};

const PLOT_HEIGHT = 220;
// Past this many bars the day labels would collide, so recharts thins them.
const DENSE_BAR_COUNT = 20;

/** The chosen operators' sum, day by day, for one KPI at a time. A losing day
 *  (a negative GGR, a share given back) is drawn below zero and in red, and
 *  zero is always on the axis, so a loss can never read as a gain. */
export function DailyChart({ summary }: Props) {
  const options = chartableKpis(summary.kpis);
  const [picked, setPicked] = useState<KpiKey | null>(null);
  const selected = picked && options.includes(picked) ? picked : options[0];
  if (!selected) return null;

  const kpi = KPI_CATALOG[selected];
  const format = (value: number) => (kpi.kind === 'money' ? formatCents(value, summary.currency) : formatCount(value));
  const data = summary.series.map((point) => ({
    day: point.day,
    tick: formatShortDay(point.day),
    value: point.figures[kpi.field] ?? 0,
  }));
  const low = Math.min(0, ...data.map((row) => row.value));
  const high = Math.max(0, ...data.map((row) => row.value));

  return (
    <section aria-label="By day" className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4">
      <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Chart figure">
        {options.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={key === selected}
            onClick={() => setPicked(key)}
            className={[
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              key === selected
                ? 'bg-[var(--co-brand)] text-white'
                : 'border border-[var(--co-border)] text-[var(--co-muted)] hover:text-[var(--co-text)]',
            ].join(' ')}
          >
            {KPI_CATALOG[key].label}
          </button>
        ))}
      </div>
      <div role="img" aria-label={`${kpi.label} by day`} style={{ height: PLOT_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--co-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="tick"
              tickLine={false}
              axisLine={{ stroke: 'var(--co-border)' }}
              interval={data.length > DENSE_BAR_COUNT ? 'preserveStartEnd' : 0}
              minTickGap={data.length > DENSE_BAR_COUNT ? 24 : 4}
              tick={{ fontSize: 10, fill: 'var(--co-muted)' }}
            />
            <YAxis
              width={88}
              domain={low === high ? ['auto', 'auto'] : [low, high]}
              tickLine={false}
              axisLine={false}
              tickFormatter={format}
              tick={{ fontSize: 10, fill: 'var(--co-muted)' }}
            />
            <Tooltip
              cursor={{ fill: 'var(--co-border)', fillOpacity: 0.35 }}
              formatter={(value) => [format(Number(value)), kpi.label]}
              labelFormatter={(label) => String(label)}
            />
            <Bar dataKey="value" radius={[2, 2, 0, 0]} isAnimationActive={false}>
              {data.map((row) => (
                <Cell key={row.day} fill={row.value < 0 ? 'var(--co-chart-red)' : 'var(--co-chart-blue)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
